import { useState, useEffect, useCallback } from 'react';
import { useRoute, useLocation } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { toastError, toastSuccess, toastInfo } from '@/lib/toast-utils';
import { useCartContext } from '@/contexts/CartContext';
import { useFavoritesContext } from '@/contexts/FavoritesContext';
import { useCart, useIsInCart } from '@/hooks/useCart';
import { ShoppingCart, Download, Heart, Share2, ZoomIn, X, ArrowLeft, ArrowRight, HeartOff } from 'lucide-react';
import { getPhotoUrl } from '@/utils/photoUtils';
import type { Photo, Event, PhotoTag } from '@/types/schema';
import { usePhoto, useRelatedPhotos, useDownloadPhoto } from '../hooks/usePhotos';
import type { PhotoWithDetails } from '../models/photo.types';

export default function PhotoDetail() {
    const [match, params] = useRoute('/photos/:photoId');
    const photoId = params?.photoId;
    const [, setLocation] = useLocation();

    const { addToCart, removeFromCart, cart, updateQuantity } = useCartContext();
    const { addToFavorites, removeFromFavorites, refreshFavorites, isFavorite } = useFavoritesContext();

    // Hooks do módulo Photos
    const { data: photo, isLoading: photoLoading, error: photoError } = usePhoto(photoId || '');
    const { data: relatedPhotos = [] } = useRelatedPhotos(photoId || '', 20);
    const downloadPhotoMutation = useDownloadPhoto();

    // Estados locais
    const [addingToCart, setAddingToCart] = useState(false);
    const [isFavorited, setIsFavorited] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);

    // Calcular se a foto está no carrinho usando o carrinho do contexto
    const cartItem = cart?.items?.find(item => item.photoId === photoId);
    const isInCart = !!cartItem;
    const cartQuantity = cartItem?.quantity || 0;

    // Scroll para o topo quando a página carregar
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [photoId]);

    // Atualizar estado de favorito quando a foto carregar
    useEffect(() => {
        if (photo?.status?.isFavorited !== undefined) {
            setIsFavorited(photo.status.isFavorited);
        }
    }, [photo]);

    // Encontrar índice da foto atual nas fotos relacionadas
    useEffect(() => {
        if (relatedPhotos.length > 0 && photoId) {
            const currentIndex = relatedPhotos.findIndex((p: Photo) => p.id === photoId);
            if (currentIndex >= 0) {
                setCurrentPhotoIndex(currentIndex);
            }
        }
    }, [relatedPhotos, photoId]);

    const handleAddToCart = async () => {
        if (!photo) return;

        try {
            setAddingToCart(true);
            await addToCart(photo.id, quantity);
            toastSuccess({ title: `${quantity} foto(s) adicionada(s) ao carrinho!` });
        } catch (error) {
            console.error('Erro ao adicionar ao carrinho:', error);
            toastError({ title: 'Erro ao adicionar foto ao carrinho' });
        } finally {
            setAddingToCart(false);
        }
    };

    const handleRemoveFromCart = async () => {
        if (!photo) return;

        try {
            await removeFromCart(photo.id);
            toastSuccess({ title: 'Foto removida do carrinho!' });
        } catch (error) {
            console.error('Erro ao remover do carrinho:', error);
            toastError({ title: 'Erro ao remover foto do carrinho' });
        }
    };

    const handleUpdateQuantity = async (newQuantity: number) => {
        if (!photo || newQuantity < 1) return;

        try {
            await updateQuantity(photo.id, newQuantity);
            setQuantity(newQuantity);
        } catch (error) {
            console.error('Erro ao atualizar quantidade:', error);
            toastError({ title: 'Erro ao atualizar quantidade' });
        }
    };

    const handleToggleFavorite = async () => {
        if (!photo) return;

        try {
            if (isFavorited) {
                await removeFromFavorites(photo.id, 'photo');
                setIsFavorited(false);
                toastSuccess({ title: 'Foto removida dos favoritos!' });
            } else {
                await addToFavorites(photo.id, 'photo');
                setIsFavorited(true);
                toastSuccess({ title: 'Foto adicionada aos favoritos!' });
            }
        } catch (error) {
            console.error('Erro ao alterar favorito:', error);
            toastError({ title: 'Erro ao alterar favorito' });
        }
    };

    const handleDownload = () => {
        if (!photoId) return;
        downloadPhotoMutation.mutate({ photoId });
    };

    const handleShare = () => {
        if (navigator.share && photo) {
            navigator.share({
                title: `Foto do evento ${photo.event?.title || 'FotUp'}`,
                text: 'Confira esta foto incrível!',
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            toastInfo({ title: 'Link copiado para a área de transferência!' });
        }
    };

    const openLightbox = (index: number) => {
        setCurrentPhotoIndex(index);
        setLightboxOpen(true);
    };

    const navigateLightbox = (direction: 'prev' | 'next') => {
        if (relatedPhotos.length === 0) return;

        if (direction === 'prev') {
            setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : relatedPhotos.length - 1);
        } else {
            setCurrentPhotoIndex(prev => prev < relatedPhotos.length - 1 ? prev + 1 : 0);
        }
    };

    const currentLightboxPhoto = relatedPhotos[currentPhotoIndex];

    if (photoLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                        <Skeleton className="aspect-[4/3] w-full rounded-lg" />
                        <div className="flex gap-2">
                            <Skeleton className="h-10 w-24" />
                            <Skeleton className="h-10 w-24" />
                            <Skeleton className="h-10 w-24" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Skeleton className="h-8 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-10 w-32" />
                    </div>
                </div>
            </div>
        );
    }

    if (photoError || !photo) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card>
                    <CardContent className="p-8 text-center">
                        <h2 className="text-2xl font-bold mb-4">Foto não encontrada</h2>
                        <p className="text-muted-foreground mb-4">
                            A foto que você está procurando não existe ou foi removida.
                        </p>
                        <Button onClick={() => setLocation('/')}>
                            Voltar ao início
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Imagem principal */}
                <div className="space-y-4">
                    <div className="relative group">
                        <img
                            src={getPhotoUrl(photo.storageurl || '')}
                            alt={photo.filename}
                            className="w-full aspect-[4/3] object-cover rounded-lg cursor-pointer"
                            onClick={() => openLightbox(currentPhotoIndex)}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <Button
                                variant="secondary"
                                size="icon"
                                onClick={() => openLightbox(currentPhotoIndex)}
                            >
                                <ZoomIn className="h-6 w-6" />
                            </Button>
                        </div>
                    </div>

                    {/* Miniaturas das fotos relacionadas */}
                    {relatedPhotos.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            {relatedPhotos.slice(0, 6).map((relatedPhoto, index) => (
                                <img
                                    key={relatedPhoto.id}
                                    src={getPhotoUrl(relatedPhoto.thumbnailurl || '')}
                                    alt={relatedPhoto.filename}
                                    className={`w-16 h-16 object-cover rounded cursor-pointer border-2 transition-colors ${relatedPhoto.id === photo.id ? 'border-primary' : 'border-transparent'
                                        }`}
                                    onClick={() => openLightbox(index)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Informações da foto */}
                <div className="space-y-6">
                    <div>
                        <h1 className="text-2xl font-bold mb-2">{photo.filename}</h1>
                        {photo.event && (
                            <div className="space-y-2">
                                <h2 className="text-lg font-semibold">{photo.event.title}</h2>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span>{new Date(photo.event.date).toLocaleDateString('pt-BR')}</span>
                                    {photo.event.location && <span>{photo.event.location}</span>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Preço e monetização */}
                    {photo.event && (
                        <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-primary">
                                R$ {photo.event.price?.toFixed(2) || '0.00'}
                            </span>
                            <Badge variant="secondary">
                                {photo.event.monetizationType === 'sale' ? 'Venda' : 'Gratuito'}
                            </Badge>
                        </div>
                    )}

                    {/* Ações */}
                    <div className="space-y-4">
                        {photo.event?.price ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium">Quantidade:</label>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleUpdateQuantity(quantity - 1)}
                                            disabled={quantity <= 1}
                                        >
                                            -
                                        </Button>
                                        <span className="w-8 text-center">{quantity}</span>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleUpdateQuantity(quantity + 1)}
                                        >
                                            +
                                        </Button>
                                    </div>
                                </div>

                                {isInCart ? (
                                    <div className="space-y-2">
                                        <Button
                                            onClick={handleRemoveFromCart}
                                            variant="outline"
                                            className="w-full"
                                        >
                                            <ShoppingCart className="h-4 w-4 mr-2" />
                                            Remover do Carrinho ({cartQuantity})
                                        </Button>
                                        <Button
                                            onClick={() => setLocation('/cart')}
                                            className="w-full"
                                        >
                                            Ir para Carrinho
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={handleAddToCart}
                                        disabled={addingToCart}
                                        className="w-full"
                                    >
                                        <ShoppingCart className="h-4 w-4 mr-2" />
                                        {addingToCart ? 'Adicionando...' : 'Adicionar ao Carrinho'}
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Button
                                onClick={handleDownload}
                                disabled={downloadPhotoMutation.isPending}
                                className="w-full"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                {downloadPhotoMutation.isPending ? 'Baixando...' : 'Baixar Foto'}
                            </Button>
                        )}

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={handleToggleFavorite}
                                className="flex-1"
                            >
                                {isFavorited ? (
                                    <>
                                        <HeartOff className="h-4 w-4 mr-2" />
                                        Remover Favorito
                                    </>
                                ) : (
                                    <>
                                        <Heart className="h-4 w-4 mr-2" />
                                        Adicionar Favorito
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleShare}
                                className="flex-1"
                            >
                                <Share2 className="h-4 w-4 mr-2" />
                                Compartilhar
                            </Button>
                        </div>
                    </div>

                    {/* Metadados */}
                    <div className="space-y-2 text-sm text-muted-foreground">
                        <div>Resolução: {photo.resolution}</div>
                        <div>Enviado em: {photo.uploadedat ? new Date(photo.uploadedat).toLocaleDateString('pt-BR') : 'N/A'}</div>
                        {photo.author && <div>Fotógrafo: {typeof photo.author === 'string' ? photo.author : photo.author.name}</div>}
                    </div>
                </div>
            </div>

            {/* Lightbox */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogContent className="max-w-7xl w-full h-full max-h-[90vh] p-0">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-4 right-4 z-10"
                            onClick={() => setLightboxOpen(false)}
                        >
                            <X className="h-6 w-6" />
                        </Button>

                        {currentLightboxPhoto && (
                            <>
                                <img
                                    src={getPhotoUrl(currentLightboxPhoto.storageurl || '')}
                                    alt={currentLightboxPhoto.filename}
                                    className="max-w-full max-h-full object-contain"
                                />

                                {relatedPhotos.length > 1 && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute left-4 top-1/2 -translate-y-1/2"
                                            onClick={() => navigateLightbox('prev')}
                                        >
                                            <ArrowLeft className="h-6 w-6" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-4 top-1/2 -translate-y-1/2"
                                            onClick={() => navigateLightbox('next')}
                                        >
                                            <ArrowRight className="h-6 w-6" />
                                        </Button>
                                    </>
                                )}

                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded">
                                    {currentPhotoIndex + 1} / {relatedPhotos.length}
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
